<template>
  <div ref="timeline" class="m-timeline">
    <div class="m-timeline__arrow --prev"
         v-show="displayed.length"
         v-on:click="!isChangeSequence && $emit('scrollPrevious')"
         :style="{
            top: (top - 80) + 'px'
         }"></div>
    <div class="m-timeline__arrow --next"
         v-show="displayed.length"
         v-on:click="!isChangeSequence && $emit('scrollNext')"
         :style="{
           bottom: (top - 80) + 'px'
         }"></div>
    <div class="m-timeline__date"
         v-bind:class="{ '--active' : !isChangeSequence }">
      <span class="m-timeline__day">{{ displayedDate | day }}</span>
      <span class="m-timeline__hour">{{ displayedDate | hour }}</span>
    </div>
    <div class="m-timeline__wrapper"
         v-bind:style="wrapperStyle"
         v-bind:class="{
            '--changing': isChangeSequence
         }">
      <ul class="m-timeline__list"
          v-bind:style="listStyle">
        <li class="m-timeline__sequence"
            :class="{
                '--in': sequence.id === nextId,
             }"
            v-for="(sequence, index) in displayed"
            v-on:click="!isChangeSequence && goToSequence(sequence)"
            v-bind:key="index - displayed.length">
          <div class="m-timeline__line"
               :class="{
                  '--reading': index < displayed.length + displayedIndex,
               }"></div>
          <div class="m-timeline__hover">
            <span class="m-timeline__hoverContent">{{ sequence.title | ellipse }}</span>
          </div>
        </li>
        <li class="m-timeline__sequence"
            :class="{
                '--in': sequence.id === nextId,
                '--active': displayedSequence && sequence.id === displayedSequence.id
                  && !isChangeSequence,
                '--out': sequence.id === previousId,
              }"
            v-for="(sequence, index) in displayed"
            v-on:click="!isChangeSequence && goToSequence(sequence)"
            v-bind:key="index">
          <div class="m-timeline__line"
               :class="{
                     '--reading': index <= displayedIndex,
                   }"
               v-bind:style="displayedSequence &&
                  sequence.id === displayedSequence.id ? activeStyle : {}"></div>
          <div class="m-timeline__hover">
            <span class="m-timeline__hoverContent">{{ sequence.title | ellipse }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Resize from 'parcel-rxjs-threejs-pack/src/app/core/Resize';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { TimelineLite, Power1 } from 'gsap';

const HEIGHT = 45;
const ELLIPSIS = 70;

// '--in': currentSequence && sequence.id === currentSequence.id && starting,
//   '--out': displayedSequence &&
// currentSequence &&
// sequence.id === displayedSequence.id &&
// currentSequence.id !== displayedSequence.id,
export default {
  name: 'timeline',
  data() {
    return {
      starting: false,
      time: 0,
      offset: 0,
      top: 0,
      size: 3,
      displayed: [],
      displayedIndex: 0,
      displayedDate: undefined,
      timeline: new TimelineLite({
        autoRemoveChildren: true,
        paused: true,
      }),
      unsubcribers: [],
      previousId: undefined,
      nextId: undefined,
      previousWheel: 0,
    };
  },
  computed: {
    ...mapGetters([
      'loading',
      'isPlaying',
      'sequences',
      'displayedSequence',
      'currentSequence',
      'isChangeSequence',
      'startColor',
    ]),
    wrapperStyle() {
      return {
        height: `${this.height}px`,
      };
    },
    listStyle() {
      return {
        top: `${this.offset}px`,
      };
    },
    activeStyle() {
      const t = Math.round(this.time);
      return {
        background: `linear-gradient(to right, ${this.startColor}, ${this.startColor} ${t}%, #dedede ${t}%, #dedede)`,
      };
    },
    middle() {
      return Math.ceil(this.displayed.length / 2);
    },
    height() {
      return this.size * HEIGHT;
    },
  },
  methods: {
    ...mapMutations([
      'play',
      'pause',
      'setChanging',
    ]),
    ...mapActions([
      'playSequence',
    ]),
    updateTimeline() {
      this.updateHeight();
      this.updateDisplayed();
    },
    updateDisplayed() {
      this.displayed = this.sequences.map((sequence, index, sequences) => {
        const position = index + sequences.length + sequences.indexOf(this.currentSequence);
        return sequences[position % sequences.length];
      });
      this.offset = (this.middle + Math.ceil((this.displayed.length - this.size) / 2)) * -HEIGHT;
    },
    updateHeight() {
      const max = Math.floor((this.$refs.timeline.offsetHeight - 160) / HEIGHT);
      const min = Math.min(max, this.sequences.length);
      this.size = min - 1 - (min % 2);
      this.top = (window.innerHeight - (this.size * HEIGHT)) / 2;
    },
    goToSequence(sequence) {
      this.playSequence(sequence);
    },
  },
  filters: {
    day(date) {
      if (!date) {
        return '';
      }

      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);

      if (today.getDate() === date.getDate() && today.getDate() === date.getDate()) {
        return 'Aujourd\'hui';
      } else if (yesterday.getDate() === date.getDate() && yesterday.getDate() === date.getDate()) {
        return 'Hier';
      }

      return `${(`0${date.getDate()}`).slice(-2)}—${(`0${date.getMonth() + 1}`).slice(-2)}`;
    },
    hour(date) {
      if (!date) {
        return '';
      }
      return `${(`0${date.getHours()}`).slice(-2)}H${(`0${date.getMinutes()}`).slice(-2)}`;
    },
    ellipse(name = '') {
      return name.substring(0, ELLIPSIS - 3) + (name.length > ELLIPSIS ? '...' : name.substring(ELLIPSIS - 3, ELLIPSIS));
    },
  },
  watch: {
    isPlaying(isPlaying) {
      this.timeline[isPlaying ? 'play' : 'pause']();
    },
    sequences() {
      this.updateTimeline();
      this.displayedDate = undefined;
    },
    displayedSequence(newDisplayedSequence, oldDisplayedSequence) {
      let duration = newDisplayedSequence.duration / 1000;
      const diff = this.timeline.totalDuration() - this.timeline.time();
      this.displayedIndex = this.displayed.indexOf(newDisplayedSequence);

      if (this.displayedIndex > this.middle) {
        this.displayedIndex -= this.displayed.length;
      }

      if (diff > 0.5) {
        duration += 3;
      }

      this.timeline.pause();
      this.timeline.clear();
      this.timeline.restart();

      this.time = 0;
      this.nextId = newDisplayedSequence.id;

      if (oldDisplayedSequence) {
        this.previousId = oldDisplayedSequence.id;
      }

      this.setChanging(true);

      // TOTAL : 9s
      this.timeline
        .to(this.$data, 1, {
          offset: this.offset + (-this.displayedIndex * HEIGHT),
          ease: Power1.easeInOut,
          onComplete: () => {
            this.setChanging(false);
            this.displayedDate = newDisplayedSequence.published;
            this.nextId = undefined;
            this.previousId = undefined;
            this.updateDisplayed();
            this.displayedIndex = 0;
          },
        })
        .to(this.$data, duration - 1, {
          ease: Power1.easeInOut,
          time: 100,
        });
    },
  },
  created() {
    this.unsubcribers = [
      Resize.subscribe(() => this.updateHeight()),
    ];
  },
  mounted() {
    this.unsubcribers = [
      Resize.subscribe(() => this.updateTimeline()),
    ];
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  $itemWidth: 16px;
  $hoverWidth: 325px;

  .m-timeline {
    text-align: center;
    color: black;

    &:before {
      content: '';
      display: inline-block;
      vertical-align: middle;
      height: 100%;
      width: 0;
    }
    &__date {
      position: absolute;
      top: 50%;
      bottom: 50%;
      height: 0;
      // right: 110px;
      // font-size: 16px;
      font-family: TrioGrotesk, sans-serif;
      right: 96px;
      font-size: 12px;
      color: #484848;
      line-height: 18px;


      &.--active {
        .m-timeline__day,
        .m-timeline__hour {
          opacity: 1;
        }
      }
    }
    &__day {
      position: absolute;
      right: 0;
      bottom: 100%;
      opacity: 0;
      transition: .8s opacity;
      text-transform: uppercase;
      white-space: nowrap;
    }
    &__hour {
      position: absolute;
      right: 0;
      top: 100%;
      opacity: 0;
      transition: .8s opacity;
      text-transform: uppercase;
    }
    &__wrapper {
      position: relative;
      display: inline-block;
      vertical-align: middle;
      overflow: hidden;
      text-align: center;
      width: 80px;

      &:not(.--changing):hover {
        padding-left: $hoverWidth;

        .m-timeline__sequence:not(.--active) {
          cursor: pointer;
        }
      }

      &.--changing {
        padding-left: 0;

        .m-timeline__sequence {
          height: 1px !important;
          padding-bottom: 22px !important;
        }
      }
    }
    &__list {
      position: absolute;

      margin: 0;
      padding: 0;

      list-style: none;
    }
    &_arrow {
      position: absolute;
      top: 100%;
    }
    &__sequence {
      position: relative;

      display: block;
      padding: 22px 32px;
      width: $itemWidth;
      height: 1px;

      &.--in,
      &.--active {
        padding-left: 0;
        padding-right: 0;
        width: 100%;
      }

      &.--in {
        transition: padding ease-in 0.8s, width ease-in 0.8s;
      }

      &.--active {
        transition: all 0s;
        padding-bottom: 21px;
        height: 2px;
      }

      &.--out {
        transition: padding ease-out 0.8s, width ease-out 0.8s;
        padding: 22px 32px;
        width: $itemWidth;
        height: 1px;
      }

      &:hover {
        padding-bottom: 21px;
        height: 2px;

        &:not(.--active):not(.--in):not(.--out) .m-timeline__hoverContent {
          transition: top ease-in 0.3s;
          top: 0;
        }

        .m-timeline__line {
          opacity: 1;
        }
      }
    }

    &__line {
      height: 100%;
      background-color: #ddd;
      margin-right: 0;
      transition: margin ease-out 0.3s;

      &.--reading {
        background-color: #484848;
      }
    }

    &__arrow {
      position: absolute;
      right: 0;
      z-index: 1;
      height: 80px;
      width: 80px;

      &.--next {
        bottom: 0;
        background-repeat: no-repeat;
        background-position: center 16px;
        background-size: 16px auto;
        background-origin: content-box;
        background-image: url('../../assets/images/next.png');
        opacity: 0.5;
      }

      &.--prev {
        top: 0;
        background-repeat: no-repeat;
        background-position: center 58px;
        background-origin: content-box;
        background-size: 16px auto;
        background-image: url('../../assets/images/previous.png');
        opacity: 0.85;
      }
    }

    &__hover {
      position: absolute;
      width: $hoverWidth;
      right: 100%;
      height: 45px;
      top: 0;
      overflow: hidden;

      &Content {
        text-align: right;
        font-family: Sailec;
        position: absolute;
        top: 100%;
        right: 20px;
        font-size: 16px;
        line-height: 24px;
        transition: top ease-out 0.2s;

        @media screen and (max-width: 1450px) {
          font-size: 14px;
          line-height: 21px;
        }

        @media screen and (max-width: 1200px) {
          font-size: 13px;
          line-height: 19.5px;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Player/Timeline.vue